/**
 * Customizations:
 * - riga 5: aggiunto `shallowEqual` all'import di react-redux
 * - righe 45-50: aggiunto `shallowEqual` come secondo argomento di `useSelector` per `profilingConfig`
 * - righe 60-63: separato il fallback `?? defaultPreferences` fuori dal selettore per evitare
 *   re-render infiniti in StrictMode: `defaultPreferences` è un nuovo riferimento ad ogni render
 *   (perché `cookies` è ricreato ogni volta), quindi tenerlo dentro il selettore causa loop.
 */
import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { useIntl, defineMessages } from 'react-intl';
import {
  displayBanner,
  updateGdprPrivacyConsent,
} from 'volto-gdpr-privacy/actions';

import {
  usePanelConfigAndPreferences,
  GDPRCookies,
  getLocaleConf,
} from 'volto-gdpr-privacy/helpers';

import 'volto-gdpr-privacy/components/ConditionalEmbed/conditional-embed.css';

const messages = defineMessages({
  conditionalEmbedAcceptCookiesDefaultDescription: {
    id: 'volto-gdpr-privacy-conditional-embed-default-description',
    defaultMessage:
      'Please, accept {cookie_type} cookies to view this content.',
  },
  conditionalEmbedAcceptCookiesAcceptMessage: {
    id: 'volto-gdpr-privacy-conditional-embed-accept-message',
    defaultMessage: '{enable_cookie_button}, or {manage_preferences_button}',
  },
  specificCookieLink: {
    id: 'volto-gdpr-privacy-conditional-embed-specific-cookie-link',
    defaultMessage: 'Enable {cookie_type} cookies',
  },
  genericCookieLink: {
    id: 'volto-gdpr-privacy-conditional-embed-generic-cookie-link',
    defaultMessage: 'manage your cookie preferences',
  },
});

const ConditionalEmbed = ({ code, url, children }) => {
  const intl = useIntl();
  const cookies = new GDPRCookies();
  const embed = code ?? url ?? '';
  const dispatch = useDispatch();

  const { defaultPreferences } = usePanelConfigAndPreferences(cookies);

  // shallowEqual evita che .filter() restituisca un nuovo riferimento ad ogni render,
  // impedendo all'useEffect sottostante di girare all'infinito quando nessun URL matcha.
  const profilingConfig = useSelector(
    (state) =>
      state.gdprPrivacyConfig?.config?.profiling?.choices?.filter(
        (c) => c?.referenceUrls?.length > 0,
      ),
    shallowEqual,
  );

  // Il fallback ?? defaultPreferences è fuori dal selettore: defaultPreferences è un nuovo
  // riferimento ad ogni render (cookies = new GDPRCookies()), tenerlo dentro causava loop infinito
  // in StrictMode perché useSelector vedeva un risultato diverso ad ogni ciclo.
  const gdprPreferencesFromStore = useSelector(
    (state) => state.gdprPrivacyConsent.preferences,
    shallowEqual,
  );
  const gdprPreferences = gdprPreferencesFromStore ?? defaultPreferences;
  const [urlReferenceConfig, setUrlReferenceConfig] = useState(null);

  useEffect(() => {
    if (profilingConfig && !urlReferenceConfig) {
      let conditionalConfig = profilingConfig.filter(
        (c) =>
          c.referenceUrls.filter((r) => embed?.indexOf(r) >= 0)?.length > 0,
      );

      setUrlReferenceConfig(conditionalConfig?.[0] ?? null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profilingConfig, setUrlReferenceConfig, urlReferenceConfig, embed]);

  //return value
  let ret = <></>;
  let embedDisabled = true;

  embedDisabled =
    urlReferenceConfig != null &&
    !gdprPreferences[urlReferenceConfig.config_key];

  if (__SERVER__ || !gdprPreferences) {
    return <></>;
  } else if (embedDisabled) {
    //embed disabled
    const text = getLocaleConf(urlReferenceConfig.text, intl.locale);
    const key = urlReferenceConfig.config_key;
    ret = (
      <div className="volto-gdpr-embed-disabled">
        {text.conditional_embed_text ??
          intl.formatMessage(
            messages.conditionalEmbedAcceptCookiesDefaultDescription,
            { cookie_type: key },
          )}{' '}
        {intl.formatMessage(
          messages.conditionalEmbedAcceptCookiesAcceptMessage,
          {
            enable_cookie_button: (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  cookies.set(key, true);
                  dispatch(
                    updateGdprPrivacyConsent({
                      ...gdprPreferences,
                      [key]: true,
                    }),
                  );
                }}
              >
                {intl.formatMessage(messages.specificCookieLink, {
                  cookie_type: key,
                })}
              </button>
            ),
            manage_preferences_button: (
              <button
                onClick={(e) => {
                  e.preventDefault();
                  dispatch(displayBanner(true, true));
                }}
              >
                {intl.formatMessage(messages.genericCookieLink)}
              </button>
            ),
          },
        )}
      </div>
    );
  } else {
    ret = <>{children}</>;
  }

  return ret;
};

export default ConditionalEmbed;
